import { useEffect, useState } from "react";
import { Card } from "../../ui/Card";
import { useOutletId, resolveOutletParam, ALL_OUTLETS_SENTINEL } from "../../lib/outlet";
import { PageHeader } from "./shared";

type Outlet = { outletId: string };
type Session = { ok: boolean; outlets?: Outlet[] };

type SpendRow = { label: string; total: number };

const fmtSg = (n: number) =>
  n.toLocaleString("en-SG", { style: "currency", currency: "SGD", maximumFractionDigits: 2 });

export function Reports() {
  const [sess, setSess] = useState<Session | null>(null);
  const selected = useOutletId(ALL_OUTLETS_SENTINEL);
  const outletParam = resolveOutletParam(selected, sess?.outlets ?? []);

  const [byCat, setByCat] = useState<SpendRow[]>([]);
  const [bySku, setBySku] = useState<SpendRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    void fetch("/api/auth/session").then((r) => r.json()).then(setSess).catch(() => {});
  }, []);

  useEffect(() => {
    if (!outletParam) return;
    setBusy(true);
    setErr(null);
    const NOW = Date.now();
    const WEEK = NOW - 84 * 86400 * 1000;
    const headers = { outletId: outletParam };
    Promise.all([
      fetch(`/api/zeemart/report/services/reports/spendings/category?outletId=${encodeURIComponent(outletParam)}&startDate=${WEEK}&endDate=${NOW}`, { headers }).then((r) => r.json()),
      fetch(`/api/zeemart/report/services/reports/spendings/sku?outletId=${encodeURIComponent(outletParam)}&startDate=${WEEK}&endDate=${NOW}`, { headers }).then((r) => r.json()),
    ])
      .then(([catRes, skuRes]) => {
        const cats = (catRes?.data ?? []) as Array<Record<string, unknown>>;
        const skus = (skuRes?.data ?? []) as Array<Record<string, unknown>>;
        setByCat(
          cats
            .map((r) => ({
              label: String(r.category ?? r.categoryName ?? "Other"),
              total: Number(r.totalSpendingOnCategoryAmount ?? r.totalamountCategory ?? r.total ?? 0),
            }))
            .filter((r) => r.total > 0)
            .sort((a, b) => b.total - a.total),
        );
        setBySku(
          skus
            .map((r) => ({
              label: String(r.skuName ?? r.name ?? r.skuId ?? "SKU"),
              total: Number(r.totalSpendingOnSkuAmount ?? r.totalamountSku ?? r.total ?? 0),
            }))
            .filter((r) => r.total > 0)
            .sort((a, b) => b.total - a.total)
            .slice(0, 12),
        );
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setBusy(false));
  }, [outletParam]);

  const maxCat = byCat.reduce((m, r) => Math.max(m, r.total), 0) || 1;
  const totalCat = byCat.reduce((s, r) => s + r.total, 0);

  return (
    <div className="space-y-7">
      <PageHeader
        title="Reports"
        subtitle="Spend across your outlets for the past 12 weeks. Real numbers from /services/reports/spendings."
      />

      {busy && <Card>Loading...</Card>}
      {err && <Card tone="bad">Could not load reports: {err}</Card>}

      {!busy && !err && (
        <>
          <section>
            <SectionLabel>Spend by category</SectionLabel>
            <Card>
              {byCat.length === 0 ? (
                <p className="text-ink-mute text-sm">
                  No category spend recorded for this range across the picked outlet(s).
                </p>
              ) : (
                <>
                  <div className="flex items-baseline justify-between mb-4">
                    <div className="text-xs text-ink-mute uppercase tracking-wider">Total</div>
                    <div className="text-2xl font-medium text-ink tabular-nums">{fmtSg(totalCat)}</div>
                  </div>
                  <div className="space-y-2">
                    {byCat.map((row) => (
                      <div key={row.label} className="grid grid-cols-[12rem_1fr_7rem] gap-3 items-center">
                        <div className="text-sm text-ink-2 truncate">{row.label}</div>
                        <div className="h-2.5 rounded-full bg-mint/40 overflow-hidden">
                          <div
                            className="h-full bg-nomni-green rounded-full transition-[width]"
                            style={{ width: `${Math.max(2, (row.total / maxCat) * 100)}%` }}
                          />
                        </div>
                        <div className="text-sm text-ink text-right tabular-nums">{fmtSg(row.total)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </section>

          <section>
            <SectionLabel>Top SKUs</SectionLabel>
            <Card>
              {bySku.length === 0 ? (
                <p className="text-ink-mute text-sm">
                  No SKU-level spend recorded for this range. As Aiko reads more orders, this table fills in.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-mute">
                      <th className="text-left py-2">SKU</th>
                      <th className="text-right py-2">Spend (past 12 weeks)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bySku.map((row) => (
                      <tr key={row.label} className="border-b border-line/60 last:border-0">
                        <td className="py-2 text-ink">{row.label}</td>
                        <td className="py-2 text-ink text-right tabular-nums">{fmtSg(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </section>
        </>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs uppercase tracking-wider text-ink-mute font-medium mb-3">{children}</h2>
  );
}
